// The starter mount: a Woolcloud, a round little sheep that trots on the ground and floats like a cloud when
// you fly. Drawn in two layers so the rider sits *in* the wool: the body first, then the rider, then the front
// tuft over the rider's legs.
import { INK, inEll, sprite } from '../../legacy/art.gen.js';

const WOOL = '#fbf7ee', WOOL2 = '#efe6d6', WOOL3 = '#ddd0bb', FACE = '#5a4a44', FACE2 = '#7a6a60', LEG = '#4a3c36';
const puffy = (x: number, y: number, cx: number, cy: number, rx: number, ry: number) => {
  // a lumpy outline: an ellipse with bumps around the edge
  const a = Math.atan2(y + 0.5 - cy, x + 0.5 - cx), bump = 1 + 0.12 * Math.sin(a * 7);
  return inEll(x, y, cx, cy, rx * bump, ry * bump);
};
const shade = (x: number, y: number, cy: number) => ((x * 3 + y * 5) % 11 === 0 ? WOOL3 : y > cy + 3 ? WOOL2 : WOOL);
type Px = (x: number, y: number) => string | null;
/** `inner` layers sit inside the body's outline, so they get no outline of their own (else a box shows through). */
const make = (w: number, h: number, f: Px, inner = false) => sprite(w, h, f, inner ? { noOutline: true } : {}) as HTMLCanvasElement;

function side(legs: boolean, front: boolean): HTMLCanvasElement {
  return make(28, 19, (x, y) => {
    if (inEll(x, y, 23, 9.5, 3.6, 3.2)) return front ? null : (x === 24 && y === 8 ? INK : x >= 25 && y === 10 ? FACE2 : FACE);   // the face
    if (!front && inEll(x, y, 21, 6.5, 1.8, 1.2)) return FACE2;                                                           // an ear
    if (puffy(x, y, 12.5, 9, 11, 7)) { if (front && (y < 7 || y > 12 || x < 6 || x > 19)) return null; return shade(x, y, 9); }
    if (!front && legs && y >= 15 && (x === 6 || x === 9 || x === 16 || x === 19)) return LEG;
    return null;
  }, front);
}
function facing(towards: boolean, legs: boolean, front: boolean): HTMLCanvasElement {
  if (!front) return facingPart(towards, legs, false, 'all');
  // the front layer: the wool over the rider's legs has no outline of its own; the face does
  const out = facingPart(towards, legs, true, 'wool'), c = out.getContext('2d')!;
  if (towards) c.drawImage(facingPart(towards, legs, true, 'head'), 0, 0);
  return out;
}
function facingPart(towards: boolean, legs: boolean, front: boolean, part: 'all' | 'wool' | 'head'): HTMLCanvasElement {
  return make(26, 21, (x, y) => {
    if (part === 'wool') { if (towards && (inEll(x, y, 13, 13.5, 4.6, 4.2) || inEll(x, y, 7.5, 11, 2.3, 1.3) || inEll(x, y, 18.5, 11, 2.3, 1.3))) return null; }
    if (towards) {
      // the face sits in front of the rider, so it belongs to the front layer
      const head = inEll(x, y, 13, 13.5, 4.6, 4.2), ear = inEll(x, y, 7.5, 11, 2.3, 1.3) || inEll(x, y, 18.5, 11, 2.3, 1.3);
      if (head) return !front ? null : (y === 13 && (x === 11 || x === 15)) ? INK : (y === 15 && x === 13) ? '#e88a9a' : (y >= 14 && (x === 10 || x === 16)) ? '#f6c9c4' : '#f3e3cf';
      if (ear) return front ? FACE : null;
    } else if (!front && inEll(x, y, 13, 16, 1.8, 1.5)) return WOOL3;                                                  // a little tail
    if (part === 'head') return null;
    if (puffy(x, y, 13, 9.5, 12, 7)) { if (front && (y < 6 || y > 11 || x < 6 || x > 20)) return null; return shade(x, y, 9); }
    if (!front && legs && y >= 17 && (x === 7 || x === 10 || x === 16 || x === 19)) return LEG;
    return null;
  }, part === 'wool');
}
const SPR = {
  side: side(true, false), sideAir: side(false, false), sideFront: side(false, true),
  down: facing(true, true, false), downAir: facing(true, false, false), downFront: facing(true, false, true),
  up: facing(false, true, false), upAir: facing(false, false, false), upFront: facing(false, false, true),
};

/** How far above the ground a rider sits (pixels). */
export const SEAT = 11;

/** Draw the mount at feet position (x, y) lifted by `lift` pixels. `layer` 'back' goes under the rider, 'front' over. */
export function drawMount(ctx: CanvasRenderingContext2D, x: number, y: number, lift: number, dir: number, flying: boolean, moving: boolean, now: number, layer: 'back' | 'front', seed = 0): void {
  const t = now / 1000 + seed * 0.37;
  const bob = flying ? Math.round(Math.sin(t * 2.4) * 1.5) : moving ? -Math.round(Math.abs(Math.sin(t * 11))) : 0;
  const key = dir === 0 ? 'down' : dir === 2 ? 'up' : 'side';
  const img = layer === 'front' ? SPR[(key + 'Front') as keyof typeof SPR] : SPR[(key + (flying ? 'Air' : '')) as keyof typeof SPR];
  const X = Math.round(x), Y = Math.round(y - lift + bob);
  ctx.save();
  ctx.translate(X, Y);
  if (dir === 3) ctx.scale(-1, 1);
  ctx.drawImage(img, -Math.round(img.width / 2) + (key === 'side' ? -1 : 0), -img.height + 1);
  ctx.restore();
  // little cloud puffs drifting down from under a flying Woolcloud
  if (flying && layer === 'back') for (let k = 0; k < 3; k++) {
    const ph = (t * 0.9 + k / 3) % 1;
    ctx.fillStyle = `rgba(251,247,238,${0.8 * (1 - ph)})`;
    ctx.beginPath(); ctx.arc(X - 6 + k * 6 + Math.sin(t * 3 + k) * 2, Y + 1 + ph * 9, 1.6 + ph * 2, 0, Math.PI * 2); ctx.fill();
  }
}

/** A shadow on the ground that shrinks and fades the higher you are. */
export function drawLiftShadow(ctx: CanvasRenderingContext2D, x: number, y: number, lift: number, wide: number): void {
  const k = Math.max(0.55, 1 - lift / 160), X = Math.round(x), Y = Math.round(y);
  ctx.fillStyle = `rgba(47,42,38,${0.26 * k + 0.08})`;
  ctx.beginPath(); ctx.ellipse(X, Y, wide * k, 2.5 * k + 0.5, 0, 0, Math.PI * 2); ctx.fill();
}
